/**
 * Session-end signalling between the auth layer and the UI.
 *
 * A session ends when Keycloak refuses the refresh token (HTTP 400): the
 * sign-in reached its maximum length, it went idle (the device slept),
 * or — one active session per user — the same account signed in on
 * another device. Keycloak answers all three the same way, so the
 * reason is inferred on the client (`sessionEndReason`). Rather than
 * bouncing straight to the login page, the auth layer announces
 * `SESSION_ENDED_EVENT` (with the reason) so the v2 shell can say why;
 * its "sign in again" button emits `RELOGIN_EVENT`.
 *
 * A refresh that fails for any other reason (offline, Keycloak
 * restarting) is NOT a session end: the provider retries instead.
 */

export const SESSION_ENDED_EVENT = 'relay:session-ended';
export const RELOGIN_EVENT = 'relay:relogin';

export type SessionEndReason = 'replaced' | 'expired' | 'idle';

/** The realm's limits (Keycloak: SSO Session Max / Idle). */
export const SESSION_MAX_MS = 12 * 60 * 60 * 1000;
export const SESSION_IDLE_MS = 30 * 60 * 1000;

let ended = false;
let reason: SessionEndReason = 'replaced';

/** True once the session has ended; 401s after that must not redirect on their own. */
export function sessionHasEnded(): boolean {
  return ended;
}

export function sessionEndedReason(): SessionEndReason {
  return reason;
}

/** Keycloak refused the refresh token: the session is gone (not a network blip). */
export function isSessionGone(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 400;
}

/**
 * Why the session most likely ended. Keycloak can't tell us, so:
 * signed in for (about) the maximum → expired; no successful refresh
 * for longer than the idle limit (the device slept or the tab was
 * frozen) → idle; otherwise another sign-in replaced it.
 */
export function sessionEndReason(opts: {
  now: number;
  authTimeMs: number | null;
  lastRefreshMs: number | null;
}): SessionEndReason {
  const margin = 2 * 60 * 1000;
  if (opts.authTimeMs != null && opts.now - opts.authTimeMs >= SESSION_MAX_MS - margin) return 'expired';
  if (opts.lastRefreshMs != null && opts.now - opts.lastRefreshMs >= SESSION_IDLE_MS - margin) return 'idle';
  return 'replaced';
}

/**
 * Mark the session ended. Shows the notice when the v2 shell is on the
 * page (it renders the listener); otherwise falls back to `login`.
 */
export function endSession(login: () => void, why: SessionEndReason = 'replaced'): void {
  if (ended) return;
  ended = true;
  reason = why;
  if (document.querySelector('[data-testid="v2-shell"]')) {
    window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: why }));
  } else {
    login();
  }
}

/** Test seam. */
export function resetSessionEnded(): void {
  ended = false;
  reason = 'replaced';
}

/**
 * Refresh the token, retrying a failure that isn't Keycloak refusing it
 * (offline after the laptop wakes, Keycloak restarting). A refused
 * token calls `onGone` (the session ended). Resolves true when the
 * token is fresh.
 */
export async function refreshOrEnd(
  updateToken: () => Promise<unknown>,
  onGone: () => void,
  waits: number[] = [0, 2_000, 5_000],
): Promise<boolean> {
  for (const wait of waits) {
    if (wait) await new Promise((r) => setTimeout(r, wait));
    try {
      await updateToken();
      return true;
    } catch (err) {
      if (isSessionGone(err)) {
        onGone();
        return false;
      }
    }
  }
  return false;
}
